import { Playbook } from '@driftt/api-types'
import {
  Box,
  Button,
  Flex,
  Icon,
  Modal,
  Select,
  styled,
  Text,
  useToast,
} from '@driftt/dds'
import { faCheck } from '@driftt/dds.icons/light/faCheck'
import React, { useEffect, useState } from 'react'
import { RootStateOrAny, useSelector } from 'react-redux'

import {
  fetchCollaboratorAccessLevel,
  getPlaybookShareLink,
  updateCollaboratorAccessLevel,
} from 'api/playbooks'
import { PlaybookCollaboratorAccess } from 'types/Playbook'
import { trackBotFlowShareLinkCopyClick } from 'utils/tracking'

const PLAYBOOK_ACCESS_OPTIONS = [
  /* TODO: When we have commenting
  {
    label: 'Commenter',
    value: PlaybookCollaboratorAccess.COMMENT,
    description: 'Anyone with this link will can view and comment',
  }, */
  {
    label: 'Reviewer',
    value: PlaybookCollaboratorAccess.VIEW,
    description: 'Anyone with this link can view and comment',
  },
  {
    label: 'No access',
    value: PlaybookCollaboratorAccess.RESTRICTED,
    description: 'Anyone with this link will be restricted from viewing',
  },
] as const

const GreenIcon = styled(Icon, {
  color: '$green6',
})

interface SharePlaybookModalProps {
  open: boolean
  onClose: () => void
  playbook: Playbook
}
interface PlaybookCollabAccessDropdownOptions {
  label: string
  value: PlaybookCollaboratorAccess
  description: string
}

const SharePlaybookModal = ({
  open,
  onClose,
  playbook,
}: SharePlaybookModalProps) => {
  const notify = useToast()
  const orgId = useSelector((state: RootStateOrAny) => state.org.id)
  const [shareLink, setShareLink] = useState<string>()
  const [linkCopied, setLinkCopied] = useState(false)
  const defaultAccessValue: PlaybookCollaboratorAccess =
    playbook.meta.collaboratorAccessLevel || PlaybookCollaboratorAccess.VIEW
  const [accessValue, setAccessValue] = useState(defaultAccessValue)

  let timeout: NodeJS.Timeout | undefined

  useEffect(() => {
    // Refetch on every model open in case the access level has changed
    // but the playbook's meta hasn't reflected it
    if (!open) return
    fetchCollaboratorAccessLevel(orgId, playbook.id)
      .then(({ data }) => {
        setAccessValue(data.accessLevel)
      })
      .catch(() => {
        setAccessValue(defaultAccessValue)
      })
  }, [defaultAccessValue, open, orgId, playbook.id])

  useEffect(() => {
    if (timeout) {
      return clearTimeout(timeout)
    }
  }, [timeout])

  const onCopySuccess = () => {
    setLinkCopied(true)
    timeout = setTimeout(() => {
      setLinkCopied(false)
    }, 3000)
  }

  const copyLink = async () => {
    trackBotFlowShareLinkCopyClick()
    try {
      if (shareLink) {
        await navigator.clipboard.writeText(shareLink)
      } else {
        const { data } = await getPlaybookShareLink(playbook?.id)
        const { uRL: url } = data

        // Cache the link so we don't have to fetch it again.
        setShareLink(url)
        await navigator.clipboard.writeText(url)
      }

      onCopySuccess()
    } catch (e) {
      notify({
        message: "Couldn't copy link",
        variant: 'error',
      })
    }
  }

  const handleSetAccessValue = async (value: PlaybookCollaboratorAccess) => {
    setAccessValue(value)
    try {
      await updateCollaboratorAccessLevel(orgId, playbook.id, value)
    } catch (e) {
      setAccessValue(defaultAccessValue)
      notify({
        message: "Couldn't update link access",
        variant: 'error',
      })
    }
  }

  const accessDescription = PLAYBOOK_ACCESS_OPTIONS.find(
    (option) => option.value === accessValue
  )?.description

  return (
    <Modal.Root open={open} onClose={onClose}>
      <Modal.Dialog
        size="large"
        variant="info"
        fullWidth
        css={{ overflowWrap: 'break-word !important', display: 'block' }}
      >
        <Modal.Title title={`Share "${playbook?.name?.trim()}"?`} />
        <Modal.Content scrollOverflow="outside">
          <Flex justifyContent="space-between" alignItems="center">
            <Box>
              <Text variant="bodyBold">Link access</Text>
              <Text>{accessDescription}</Text>
            </Box>
            <Select
              label="Link access"
              defaultValue={PLAYBOOK_ACCESS_OPTIONS.filter(
                (option: PlaybookCollabAccessDropdownOptions) =>
                  option.value === accessValue
              )}
              onChange={(newValue) => {
                const { value } =
                  newValue as PlaybookCollabAccessDropdownOptions
                handleSetAccessValue(value)
              }}
              options={[...PLAYBOOK_ACCESS_OPTIONS]}
              size="medium"
              hideLabel={true}
            />
          </Flex>
        </Modal.Content>
        <Modal.Actions alignItems="center">
          {linkCopied && (
            <Text>
              <GreenIcon icon={faCheck} /> Link copied
            </Text>
          )}
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button variant="primary" onClick={copyLink}>
            Copy link
          </Button>
        </Modal.Actions>
      </Modal.Dialog>
    </Modal.Root>
  )
}

export default SharePlaybookModal
